import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  FilterList,
  Restaurant,
  Close
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

const MealsForYou = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meals, setMeals] = useState([]);
  const [filteredMeals, setFilteredMeals] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    mealType: '',
    dietaryType: [],
    cuisineStyle: [],
    tags: []
  });
  const [preferences, setPreferences] = useState({
    mealTypes: [],
    dietaryTypes: [],
    cuisineStyles: [],
    tags: []
  });
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all meals
  const fetchMeals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters based on filters
      const queryParams = new URLSearchParams();
      if (filters.mealType) queryParams.append('mealType', filters.mealType);
      if (filters.dietaryType.length) queryParams.append('dietaryType', filters.dietaryType.join(','));
      if (filters.cuisineStyle.length) queryParams.append('cuisineStyle', filters.cuisineStyle.join(','));
      if (filters.tags.length) queryParams.append('tags', filters.tags.join(','));
      
      const url = `${API_BASE_URL}/meals/all${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axios.get(url);
      
      setMeals(response.data);
      setFilteredMeals(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching meals:', error);
      setError(error.response?.data?.message || 'Failed to fetch meals');
      setLoading(false);
    }
  };

  // Fetch system preferences for filter options
  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/preferences`);
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  // Toggle favorite status
  const toggleFavorite = async (meal) => {
    try {
      if (meal.isFavorite) {
        // Remove from favorites
        await axios.delete(`${API_BASE_URL}/meals/favorites/${meal._id}`);
        
        // Update local state
        setMeals(meals.map(m => {
          if (m._id === meal._id) {
            return { ...m, isFavorite: false };
          }
          return m;
        }));
        
        setFilteredMeals(filteredMeals.map(m => {
          if (m._id === meal._id) {
            return { ...m, isFavorite: false };
          }
          return m;
        }));
        
        showNotification('Removed from favorites', 'success');
      } else {
        // Add to favorites
        await axios.post(`${API_BASE_URL}/meals/favorites/${meal._id}`, {
          folder: 'Meals'
        });
        
        // Update local state
        setMeals(meals.map(m => {
          if (m._id === meal._id) {
            return { ...m, isFavorite: true };
          }
          return m;
        }));
        
        setFilteredMeals(filteredMeals.map(m => {
          if (m._id === meal._id) {
            return { ...m, isFavorite: true };
          }
          return m;
        }));
        
        showNotification('Added to favorites', 'success');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showNotification(error.response?.data?.message || 'Failed to update favorites', 'error');
    }
  };

  // Filter meals based on search query
  const filterMealsBySearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredMeals(meals);
      return;
    }
    
    const lowercaseQuery = query.toLowerCase();
    const filtered = meals.filter(meal => {
      return meal.name.toLowerCase().includes(lowercaseQuery) ||
             meal.description?.toLowerCase().includes(lowercaseQuery) ||
             meal.tags?.some(tag => tag.toLowerCase().includes(lowercaseQuery)) ||
             meal.cuisineStyle?.some(cuisine => cuisine.toLowerCase().includes(lowercaseQuery));
    });
    
    setFilteredMeals(filtered);
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      mealType: '',
      dietaryType: [],
      cuisineStyle: [],
      tags: []
    });
    setSearchQuery('');
  };

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Close notification
  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Apply filters and fetch meals
  const applyFilters = () => {
    fetchMeals();
    setFilterOpen(false);
  };

  // Initial data loading
  useEffect(() => {
    fetchMeals();
    fetchPreferences();
  }, []);

  // Filter component
  const FilterPanel = () => (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" component="h2">
          <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
          Filter Meals
        </Typography>
        <IconButton size="small" onClick={() => setFilterOpen(false)}>
          <Close />
        </IconButton>
      </Box>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="meal-type-label">Meal Type</InputLabel>
            <Select
              labelId="meal-type-label"
              value={filters.mealType}
              onChange={(e) => setFilters({ ...filters, mealType: e.target.value })}
              label="Meal Type"
            >
              <MenuItem value="">All Types</MenuItem>
              {preferences.mealTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="dietary-type-label">Dietary Type</InputLabel>
            <Select
              labelId="dietary-type-label"
              multiple
              value={filters.dietaryType}
              onChange={(e) => setFilters({ ...filters, dietaryType: e.target.value })}
              label="Dietary Type"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {preferences.dietaryTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="cuisine-style-label">Cuisine Style</InputLabel>
            <Select
              labelId="cuisine-style-label"
              multiple
              value={filters.cuisineStyle}
              onChange={(e) => setFilters({ ...filters, cuisineStyle: e.target.value })}
              label="Cuisine Style"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {preferences.cuisineStyles.map((style) => (
                <MenuItem key={style} value={style}>
                  {style.charAt(0).toUpperCase() + style.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="tags-label">Tags</InputLabel>
            <Select
              labelId="tags-label"
              multiple
              value={filters.tags}
              onChange={(e) => setFilters({ ...filters, tags: e.target.value })}
              label="Tags"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {preferences.tags.map((tag) => (
                <MenuItem key={tag} value={tag}>
                  {tag}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button onClick={resetFilters} sx={{ mr: 1 }}>
          Reset
        </Button>
        <Button variant="contained" color="primary" onClick={applyFilters}>
          Apply Filters
        </Button>
      </Box>
    </Paper>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          <Restaurant sx={{ mr: 1, verticalAlign: 'middle' }} />
          Meals For You
        </Typography>
        
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterOpen(!filterOpen)}
            sx={{ mr: 1 }}
          >
            Filters
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {filterOpen && <FilterPanel />}
      
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          label="Search meals"
          placeholder="Search by name, description, tags..."
          value={searchQuery}
          onChange={(e) => filterMealsBySearch(e.target.value)}
          InputProps={{
            endAdornment: searchQuery ? (
              <IconButton size="small" onClick={() => filterMealsBySearch('')}>
                <Close />
              </IconButton>
            ) : null
          }}
        />
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : filteredMeals.length === 0 ? (
        <Box textAlign="center" my={4}>
          <Typography variant="h6" color="textSecondary">
            No meals found matching your criteria
          </Typography>
          {(Object.values(filters).some(f => Array.isArray(f) ? f.length > 0 : f) || searchQuery) && (
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={resetFilters} 
              sx={{ mt: 2 }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredMeals.mealsWithFavoriteStatus.map((meal) => (
            <Grid item xs={12} sm={6} md={4} key={meal._id}>
              <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {meal.imageUrl ? (
                  <CardMedia
                    component="img"
                    height="180"
                    image={meal.imageUrl}
                    alt={meal.name}
                  />
                ) : (
                  <Box 
                    height="180" 
                    bgcolor="grey.200" 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center"
                  >
                    <Restaurant sx={{ fontSize: 80, color: 'grey.400' }} />
                  </Box>
                )}
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" component="h2" gutterBottom>
                      {meal.name}
                    </Typography>
                    <Tooltip title={meal.isFavorite ? "Remove from favorites" : "Add to favorites"}>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => toggleFavorite(meal)}
                      >
                        {meal.isFavorite ? <Favorite /> : <FavoriteBorder />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {meal.description || 'No description available'}
                  </Typography>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ mb: 1 }}>
                    <Chip 
                      size="small" 
                      label={meal.mealType.charAt(0).toUpperCase() + meal.mealType.slice(1)} 
                      color="primary" 
                      sx={{ mr: 1, mb: 1 }} 
                    />
                    
                    {meal.dietaryType?.map((type) => (
                      <Chip 
                        key={type} 
                        size="small" 
                        label={type.charAt(0).toUpperCase() + type.slice(1)} 
                        sx={{ mr: 1, mb: 1 }} 
                      />
                    ))}
                  </Box>
                  
                  <Box>
                    {meal.cuisineStyle?.slice(0, 3).map((style) => (
                      <Chip 
                        key={style} 
                        size="small" 
                        label={style} 
                        variant="outlined" 
                        sx={{ mr: 1, mb: 1 }} 
                      />
                    ))}
                    
                    {meal.tags?.slice(0, 2).map((tag) => (
                      <Chip 
                        key={tag} 
                        size="small" 
                        label={tag} 
                        variant="outlined" 
                        color="secondary" 
                        sx={{ mr: 1, mb: 1 }} 
                      />
                    ))}
                    
                    {((meal.cuisineStyle?.length || 0) > 3 || (meal.tags?.length || 0) > 2) && (
                      <Chip 
                        size="small" 
                        label="..." 
                        variant="outlined" 
                        sx={{ mb: 1 }} 
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      <Snackbar 
        open={notification.open} 
        autoHideDuration={4000} 
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MealsForYou;
